/**
 * Nether portal rules (pure; no DOM, usable from smoke tests).
 *
 *  - findPortalFrame: vanilla PortalShape — an obsidian frame whose interior is
 *    2x3 .. 21x21 (outer frame 4x5 .. 23x23), corners optional, interior empty
 *    (air / fire / portal), along the X or Z axis.
 *  - portalCellsToBreak: the connected portal blob that loses its frame when a
 *    frame block (or a portal cell) in its plane is removed.
 *  - findPortalInEdits / portal placement helpers used by dimension travel.
 */

import { AIR, FIRE_ID, isNetherPortal, NETHER_PORTAL_ID, NETHER_PORTAL_Z_ID, OBSIDIAN_ID } from '../blocks';

export interface BlockGetter {
  getBlock(x: number, y: number, z: number): number;
}

export interface PortalFrame {
  /** 'x': the portal plane spans x and y (constant z); 'z': spans z and y. */
  axis: 'x' | 'z';
  /** Lowest interior corner (min x/z, bottom y). */
  x: number; y: number; z: number;
  width: number;
  height: number;
}

export const PORTAL_MIN_W = 2;
export const PORTAL_MIN_H = 3;
export const PORTAL_MAX = 21;

const empty = (id: number): boolean => id === AIR || id === FIRE_ID || isNetherPortal(id);
/** Portal block id for a pane along `axis`. */
export const portalIdFor = (axis: 'x' | 'z'): number => (axis === 'x' ? NETHER_PORTAL_ID : NETHER_PORTAL_Z_ID);

/** Try both axes from an interior cell; null if no valid frame encloses it. */
export function findPortalFrame(w: BlockGetter, x: number, y: number, z: number): PortalFrame | null {
  return frameOnAxis(w, x, y, z, 'x') ?? frameOnAxis(w, x, y, z, 'z');
}

function frameOnAxis(w: BlockGetter, x0: number, y0: number, z0: number, axis: 'x' | 'z'): PortalFrame | null {
  const dx = axis === 'x' ? 1 : 0;
  const dz = axis === 'z' ? 1 : 0;
  const at = (u: number, y: number): number => w.getBlock(x0 + u * dx, y, z0 + u * dz);
  if (!empty(at(0, y0))) return null;
  // drop to the bottom of the interior
  let by = y0;
  for (let n = 0; n < PORTAL_MAX && empty(at(0, by - 1)); n++) by--;
  if (at(0, by - 1) !== OBSIDIAN_ID) return null;
  // left edge
  let left = 0;
  while (left > -PORTAL_MAX && empty(at(left - 1, by)) && at(left - 1, by - 1) === OBSIDIAN_ID) left--;
  if (at(left - 1, by) !== OBSIDIAN_ID) return null;
  let right = 0;
  while (right - left + 1 <= PORTAL_MAX && empty(at(right + 1, by)) && at(right + 1, by - 1) === OBSIDIAN_ID) right++;
  if (at(right + 1, by) !== OBSIDIAN_ID) return null;
  const width = right - left + 1;
  if (width < PORTAL_MIN_W || width > PORTAL_MAX) return null;
  // grow upward row by row
  let height = 0;
  for (; height <= PORTAL_MAX; height++) {
    const y = by + height;
    let rowEmpty = true;
    for (let u = left; u <= right; u++) if (!empty(at(u, y))) { rowEmpty = false; break; }
    if (!rowEmpty) break;
    if (at(left - 1, y) !== OBSIDIAN_ID || at(right + 1, y) !== OBSIDIAN_ID) return null;
  }
  if (height < PORTAL_MIN_H || height > PORTAL_MAX) return null;
  // top row must be solid obsidian
  for (let u = left; u <= right; u++) if (at(u, by + height) !== OBSIDIAN_ID) return null;
  return { axis, x: x0 + left * dx, y: by, z: z0 + left * dz, width, height };
}

/** Every interior cell of a frame. */
export function portalCells(f: PortalFrame): Array<[number, number, number]> {
  const out: Array<[number, number, number]> = [];
  for (let v = 0; v < f.height; v++) {
    for (let u = 0; u < f.width; u++) {
      out.push(f.axis === 'x' ? [f.x + u, f.y + v, f.z] : [f.x, f.y + v, f.z + u]);
    }
  }
  return out;
}

/** Axis of the portal blob through a portal cell (by its in-plane neighbours). */
export function portalAxisAt(w: BlockGetter, x: number, y: number, z: number): 'x' | 'z' {
  const self = w.getBlock(x, y, z);
  if (self === NETHER_PORTAL_Z_ID) return 'z';
  if (self === NETHER_PORTAL_ID && !isNetherPortal(w.getBlock(x, y, z + 1)) && !isNetherPortal(w.getBlock(x, y, z - 1))) return 'x';
  // portals are at least 2 wide, so every cell has a portal neighbour along its axis
  if (isNetherPortal(w.getBlock(x + 1, y, z)) || isNetherPortal(w.getBlock(x - 1, y, z))) return 'x';
  if (isNetherPortal(w.getBlock(x, y, z + 1)) || isNetherPortal(w.getBlock(x, y, z - 1))) return 'z';
  return w.getBlock(x + 1, y, z) === OBSIDIAN_ID || w.getBlock(x - 1, y, z) === OBSIDIAN_ID ? 'x' : 'z';
}

/**
 * A block at (x, y, z) just changed to `newId`. If it touches a portal in the
 * portal's own plane and is no longer obsidian/portal, the whole connected
 * portal blob loses its frame: returns those cells (empty when nothing breaks).
 */
export function portalCellsToBreak(w: BlockGetter, x: number, y: number, z: number, newId: number): Array<[number, number, number]> {
  if (newId === OBSIDIAN_ID || isNetherPortal(newId)) return [];
  const dirs: Array<[number, number, number]> = [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1]];
  for (const [dx, dy, dz] of dirs) {
    const px = x + dx, py = y + dy, pz = z + dz;
    if (!isNetherPortal(w.getBlock(px, py, pz))) continue;
    const axis = portalAxisAt(w, px, py, pz);
    // out-of-plane neighbours (standing in front of the portal) do not break it
    if ((axis === 'x' && dz !== 0) || (axis === 'z' && dx !== 0)) continue;
    return floodPortal(w, px, py, pz, axis);
  }
  return [];
}

function floodPortal(w: BlockGetter, x: number, y: number, z: number, axis: 'x' | 'z'): Array<[number, number, number]> {
  const seen = new Set<string>();
  const out: Array<[number, number, number]> = [];
  const stack: Array<[number, number, number]> = [[x, y, z]];
  while (stack.length && out.length < PORTAL_MAX * PORTAL_MAX) {
    const [cx, cy, cz] = stack.pop()!;
    const k = cx + ',' + cy + ',' + cz;
    if (seen.has(k)) continue;
    seen.add(k);
    if (!isNetherPortal(w.getBlock(cx, cy, cz))) continue;
    out.push([cx, cy, cz]);
    stack.push([cx, cy + 1, cz], [cx, cy - 1, cz]);
    if (axis === 'x') stack.push([cx + 1, cy, cz], [cx - 1, cy, cz]);
    else stack.push([cx, cy, cz + 1], [cx, cy, cz - 1]);
  }
  return out;
}

/**
 * Nearest saved portal block within `radius` (horizontal, square like vanilla's
 * POI search) of (x, z). Portals only ever come from player edits (worldgen has
 * none lit), so the save's edit list is an exact index and needs no loaded chunks.
 * `editsOf(cx, cz)` returns that chunk's edits (index -> id).
 */
export function findPortalInEdits(
  editsOf: (cx: number, cz: number) => Map<number, number> | undefined,
  x: number, z: number, radius: number, current?: BlockGetter,
): [number, number, number] | null {
  let best: [number, number, number] | null = null;
  let bestD = Infinity;
  const c0x = Math.floor((x - radius) / 16), c1x = Math.floor((x + radius) / 16);
  const c0z = Math.floor((z - radius) / 16), c1z = Math.floor((z + radius) / 16);
  for (let cz = c0z; cz <= c1z; cz++) {
    for (let cx = c0x; cx <= c1x; cx++) {
      const m = editsOf(cx, cz);
      if (!m) continue;
      for (const [idx, id] of m) {
        if (!isNetherPortal(id)) continue;
        const bx = cx * 16 + (idx & 15), bz = cz * 16 + ((idx >> 4) & 15), by = Math.floor(idx / 256);
        if (Math.abs(bx - x) > radius || Math.abs(bz - z) > radius) continue;
        if (current && !isNetherPortal(current.getBlock(bx, by, bz)) && current.getBlock(bx, by, bz) !== AIR) continue;
        const d = (bx - x) ** 2 + (bz - z) ** 2 + by * 1e-3; // nearest, then lowest
        if (d < bestD) { bestD = d; best = [bx, by, bz]; }
      }
    }
  }
  return best;
}

/** Blocks to set for a new 2x3 portal (x-axis) whose interior bottom-left is (x, y, z):
 *  obsidian frame, a 3-deep obsidian platform under it and cleared air either side. */
export function newPortalBlocks(x: number, y: number, z: number): Array<[number, number, number, number]> {
  const out: Array<[number, number, number, number]> = [];
  // clear a 4x4x3 pocket in front of / behind the portal
  for (let dz = -1; dz <= 1; dz++) {
    if (dz === 0) continue;
    for (let u = 0; u < 2; u++) for (let v = 0; v < 3; v++) out.push([x + u, y + v, z + dz, AIR]);
    for (let u = -1; u <= 2; u++) out.push([x + u, y - 1, z + dz, OBSIDIAN_ID]); // platform
  }
  for (let u = -1; u <= 2; u++) {
    for (let v = -1; v <= 3; v++) {
      const frame = u === -1 || u === 2 || v === -1 || v === 3;
      out.push([x + u, y + v, z, frame ? OBSIDIAN_ID : NETHER_PORTAL_ID]);
    }
  }
  return out;
}
